/**
 * Root: decides between the login screen, the demo, and the signed-in app,
 * then renders the current page inside the Shell. Routing is hash-based
 * (#/diary) so it works on any static host, including Cloudflare Pages.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { Session } from '@supabase/supabase-js';
import { config } from './config';
import type { DataSource } from './data/source';
import { getSupabase } from './data/supabaseClient';
import { SupabaseSource } from './data/supabaseSource';
import { errorMessage, log } from './lib/logger';
import type { Profile } from './lib/types';
import { BodyPage } from './pages/BodyPage';
import { DashboardPage } from './pages/DashboardPage';
import { DiaryPage } from './pages/DiaryPage';
import { CookPage } from './pages/CookPage';
import { FoodsPage } from './pages/FoodsPage';
import { LoginPage } from './pages/LoginPage';
import { OwnerPage } from './pages/OwnerPage';
import { ProfilePage } from './pages/ProfilePage';
import { TefPage } from './pages/TefPage';
import { TrendsPage } from './pages/TrendsPage';
import { ROUTES, Shell, type Route } from './Shell';
import { AppProvider, useApp } from './state/app';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Spinner } from './components/ui';

function parseRoute(): Route {
  const h: string = window.location.hash.replace(/^#\/?/, '');
  return (ROUTES.find((r) => r.id === h)?.id ?? 'dashboard');
}

type Boot =
  | { stage: 'loading' }
  | { stage: 'signed_out' }
  | { stage: 'ready'; source: DataSource; profile: Profile }
  | { stage: 'error'; message: string };

export function App(): ReactElement {
  const [boot, setBoot] = useState<Boot>({ stage: 'loading' });

  const start = useCallback(async (source: DataSource): Promise<void> => {
    try {
      const profile: Profile = await source.getProfile();
      setBoot({ stage: 'ready', source, profile });
    } catch (err) {
      log.error('boot.profile_failed', { error: errorMessage(err) });
      setBoot({ stage: 'error', message: errorMessage(err) });
    }
  }, []);

  const startDemo = useCallback((): void => {
    // Loaded on demand: the demo bundles ~100 sample USDA foods the real app doesn't need.
    import('./data/demoSource')
      .then(({ DemoSource }) => start(new DemoSource()))
      .catch((err: unknown) => setBoot({ stage: 'error', message: `Could not load the demo: ${errorMessage(err)}` }));
  }, [start]);

  useEffect(() => {
    if (config.demoOnly) { startDemo(); return; }
    const sb = getSupabase();
    let currentUser: string | null = null;
    const onSession = (session: Session | null): void => {
      const uid: string | null = session?.user.id ?? null;
      if (uid === currentUser) return;
      currentUser = uid;
      if (session) void start(new SupabaseSource(session.user.id, session.user.email ?? ''));
      else setBoot((b) => (b.stage === 'ready' && b.source.kind === 'demo' ? b : { stage: 'signed_out' }));
    };
    sb.auth.getSession()
      .then(({ data, error }) => {
        if (error) log.warn('boot.get_session_failed', { message: error.message });
        onSession(data.session);
        if (!data.session) setBoot((b) => (b.stage === 'loading' ? { stage: 'signed_out' } : b));
      })
      .catch((err: unknown) => { log.error('boot.failed', { error: errorMessage(err) }); setBoot({ stage: 'signed_out' }); });
    const { data: sub } = sb.auth.onAuthStateChange((_event, session) => onSession(session));
    return () => sub.subscription.unsubscribe();
  }, [start, startDemo]);

  if (boot.stage === 'loading') return <div className="loading-screen"><Spinner /></div>;
  if (boot.stage === 'signed_out') return <LoginPage onDemo={startDemo} />;
  if (boot.stage === 'error') {
    return (
      <div className="loading-screen">
        <div className="card" style={{ maxWidth: 480 }}>
          <h2 style={{ marginBottom: 8 }}>Couldn’t load your account</h2>
          <p className="muted">{boot.message}</p>
          <div className="row"><button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button>
            <button type="button" className="btn" onClick={() => void getSupabase().auth.signOut().then(() => window.location.reload())}>Sign out</button></div>
        </div>
      </div>
    );
  }
  return (
    <AppProvider source={boot.source} initialProfile={boot.profile}>
      <Routed />
    </AppProvider>
  );
}

function Routed(): ReactElement {
  const [route, setRoute] = useState<Route>(parseRoute);
  const { toasts } = useApp();
  useEffect(() => {
    const onHash = (): void => setRoute(parseRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = useCallback((r: string): void => {
    const next: Route = ROUTES.find((x) => x.id === r)?.id ?? 'dashboard';
    if (window.location.hash !== `#/${next}`) window.location.hash = `/${next}`;
    setRoute(next);
    window.scrollTo({ top: 0 });
  }, []);
  return (
    <>
      <Shell route={route} go={go}>
        <ErrorBoundary key={route} where={route}>
        {route === 'dashboard' && <DashboardPage go={go} />}
        {route === 'diary' && <DiaryPage />}
        {route === 'foods' && <FoodsPage />}
        {route === 'cook' && <CookPage />}
        {route === 'body' && <BodyPage />}
        {route === 'tef' && <TefPage />}
        {route === 'trends' && <TrendsPage />}
        {route === 'profile' && <ProfilePage />}
        {route === 'owner' && <OwnerPage />}
        </ErrorBoundary>
      </Shell>
      <div className="toasts" aria-live="polite">{toasts.map((t) => <div key={t.id} className={`toast ${t.kind === 'err' ? 'err' : ''}`}>{t.text}</div>)}</div>
    </>
  );
}
